<template>
  <div
    v-if="!isIntegrationUnavailable"
    class="insights-team-collaboration-widget insights-widget"
  >
    <InsightsIntegrationFailureDialog
      v-if="showFailureDialog"
      :show="showFailureDialog"
      @close="toggleFailureDialog"
    />

    <InsightsCollaborationDataLagDialog
      v-if="showDataLagDialog"
      :show="showDataLagDialog"
      @close="showDataLagDialog = false"
    />

    <div
      class="widget-wrapper"
      :class="{ 'blurred-bg': isStillSyncing }"
    >
      <div class="widget-common widget-small widget-full-height">
        <div class="widget-title d-flex align-items-center">
          <span>{{ title }}</span>
          <i
            v-tooltip="tooltipOptions"
            class="hi hi-info-filled ml-5"
            :title="tooltip"
          />
        </div>
        <div
          class="widget-body"
          :class="bodyClasses"
        >
          <div
            v-if="integrationExists"
            class="d-flex"
            :class="isIntegrationSyncing ? null : bodyClasses"
          >
            <div
              v-if="isReady || isIntegrationSyncing"
              class="insights-team-collaboration-widget-content"
            >
              <template v-if="isIntegrationSynced">
                <template v-if="!isEmptyWidget">
                  <div class="section">
                    <InsightsTeamCollaborationPill
                      type="email"
                      property="sent_count"
                      :value="widgetData.email.sent_count"
                      :providers="providersOf('email')"
                    />
                    <InsightsTeamCollaborationPill
                      type="email"
                      property="received_count"
                      :value="widgetData.email.received_count"
                      :providers="providersOf('email')"
                    />
                    <InsightsTeamCollaborationPill
                      type="email"
                      property="read_count"
                      :value="widgetData.email.read_count"
                      :providers="providersOf('email')"
                    />
                  </div>
                  <div class="section">
                    <InsightsTeamCollaborationPill
                      type="call"
                      property="total_count"
                      :value="widgetData.call.total_count"
                      :providers="providersOf('call')"
                    />
                    <InsightsTeamCollaborationPill
                      type="call"
                      property="duration"
                      :value="widgetData.call.duration"
                      :providers="providersOf('call')"
                    />
                    <InsightsTeamCollaborationPill
                      type="message"
                      property="total_count"
                      :value="messagesSentCount"
                      :providers="providersOf('message')"
                    />
                  </div>
                  <p
                    v-if="isDataLagging"
                    class="collaboration-data-lag"
                  >
                    {{ dataLagText }}
                    <a
                      href="#"
                      @click.prevent="showDataLagDialog = true"
                      >Learn more.</a
                    >
                  </p>
                </template>
                <InsightsNoData
                  v-else
                  :type="emptyReason"
                />
              </template>
              <InsightsIntegrationSyncStatus
                v-else
                title="Synchronizing collaboration data"
                :is-still-syncing="isStillSyncing"
              />
            </div>
          </div>
          <InsightsWidgetIntegration
            v-else-if="isReady"
            :widget-key="widgetKey"
            :widget-data="widgetData"
          >
            <template #description>
              Connect your organization's communication platform to measure how your teams are engaging in collaboration
            </template>
          </InsightsWidgetIntegration>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { INSIGHTS } from '~/app_constants';
import { mapGetters } from 'vuex';
import { Insights, Integrations } from '~/mixins';
import InsightsTeamCollaborationPill from '~/components/insights/InsightsTeamCollaborationPill.vue';
import InsightsWidgetIntegration from '~/components/insights/InsightsWidgetIntegration.vue';
import InsightsIntegrationSyncStatus from '~/components/insights/InsightsIntegrationSyncStatus.vue';
import InsightsIntegrationFailureDialog from '~/components/insights/InsightsIntegrationFailureDialog.vue';
import InsightsCollaborationDataLagDialog from '~/components/insights/InsightsCollaborationDataLagDialog.vue';

export default {
  name: 'InsightsTeamCollaborationWidget',
  components: {
    InsightsTeamCollaborationPill,
    InsightsWidgetIntegration,
    InsightsIntegrationSyncStatus,
    InsightsIntegrationFailureDialog,
    InsightsCollaborationDataLagDialog,
    InsightsNoData: defineAsyncComponent(() => import('~/components/insights/InsightsNoData.vue')),
  },
  mixins: [Insights, Integrations],
  data: () => ({
    widgetKey: INSIGHTS.api.collaborations,
    tooltip: INSIGHTS.tooltips.collaborations.content,
    retryFetch: INSIGHTS.collaborations.retryFetch,
    tooltipOptions: Object.freeze({
      placement: 'top',
      html: true,
    }),
    title: 'Team Collaboration',
    showDataLagDialog: false,
  }),
  computed: {
    ...mapGetters({
      widgetData: 'collaborations',
    }),
    integrationStatus() {
      return this.widgetData?.collaboration_integration_status;
    },
    isDataLagging() {
      return this.widgetData.collaboration_lagging_integrations.length > 0;
    },
    dataLagText() {
      const lagging = this.widgetData.collaboration_lagging_integrations;
      const names = Object.entries(INSIGHTS.collaborations.lagNames)
        .filter(([type]) => lagging.includes(type))
        .map(([, name]) => name);

      return `Recent ${names.join(' and ')} data is still syncing.`;
    },
    messagesSentCount() {
      const { message } = this.widgetData;

      return message.public_message_count + message.private_message_count;
    },
  },
  async created() {
    await this.fetch();
    this.checkFailureStatus();
  },
  methods: {
    providersOf(collaborationType) {
      return this.widgetData.collaboration_providers_by_type[collaborationType];
    },
  },
};
</script>
